import { useEffect, useState } from 'react'

import type { AccordionItemProps } from './accordion.types'

import Box from '../box'
import { omitThemingProps, useStyleConfig } from '../core'
import { cs } from '../utils'
import AccordionItemArrow from './accordionItemArrow'
import { useAccordionContext } from './context'

/** An accordion item (horizontal AccordionOrientation). */
export const AccordionItemHorizontal = (props: AccordionItemProps) => {
  const { className, children, collapsed, content, disabled, id, contentPx, title, onToggle, ...rest } =
    omitThemingProps(props)
  const [isCollapsedInternal, setIsCollapsedInternal] = useState<boolean>(false)

  const styles = useStyleConfig('Accordion', useAccordionContext())

  useEffect(() => {
    setIsCollapsedInternal(!!collapsed)
  }, [collapsed])

  const toggle = () => {
    if (!disabled) {
      onToggle?.({ id, collapsed: !isCollapsedInternal })
      setIsCollapsedInternal(!isCollapsedInternal)
    }
  }

  const cursor = disabled ? 'not-allowed' : 'pointer'
  const fontWeight = typeof title === 'string' ? '500' : 'inherit'

  const expandedContentPx = contentPx ? contentPx : styles.itemHorizontal.space

  return (
    <Box
      alignItems={isCollapsedInternal ? 'normal' : 'baseline'}
      bg={disabled ? 'disabled.bg' : undefined}
      borderRight="1px solid transparent"
      className={cs('vui-accordion-item-horizontal', className)}
      color={disabled ? 'disabled.color' : undefined}
      column
      cursor={disabled ? 'not-allowed' : undefined}
      id={`${id}`}
      maxW={isCollapsedInternal ? styles.item.minH : 'auto'}
      minW={styles.item.minH}
      transitionDuration="0ms"
      userSelect={disabled ? 'none' : undefined}
      {...styles.item}
      p={0}
      {...rest}
    >
      <Box
        cursor={cursor}
        hoverBg={
          disabled
            ? 'transparent'
            : isCollapsedInternal
              ? styles.item.itemTitleHoverBg
              : styles.item.expandedItemTitleHoverBg
        }
        justifyContent="space-between"
        minH={styles.item.minH}
        onClick={toggle}
        p={styles.itemHorizontal.space}
        w="100%"
      >
        {!isCollapsedInternal && (
          <Box fontWeight={fontWeight} opacity={disabled ? 0.5 : 1}>
            {title}
          </Box>
        )}
        <AccordionItemArrow
          collapsed={isCollapsedInternal}
          color={styles.icon.color}
          disabled={disabled}
          orientation="horizontal"
        />
      </Box>

      {!isCollapsedInternal ? (
        <Box p={styles.itemHorizontal.space} pt={0} px={expandedContentPx} transitionDuration="0ms" w={1}>
          {children ?? content}
        </Box>
      ) : (
        <Box
          cursor={cursor}
          flexDirection="row-reverse"
          fontWeight={fontWeight}
          mb={styles.item.minH}
          mt={styles.itemHorizontal.space}
          onClick={toggle}
          transform="rotate(-90deg)"
          transitionDuration="0ms"
          whiteSpace="nowrap"
        >
          {title}
        </Box>
      )}
    </Box>
  )
}

AccordionItemHorizontal.displayName = 'AccordionItemHorizontal'

export default AccordionItemHorizontal
